import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../../lib/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang, setLang, dict, isRTL } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const LINKS = [
    { label: dict.nav_manifesto, href: "#manifesto", key: "manifesto" },
    { label: dict.nav_gallery, href: "#gallery", key: "gallery" },
    { label: dict.nav_catalogue, href: "#catalogue", key: "catalogue" },
    { label: dict.nav_sustainability, href: "#sustainability", key: "sustainability" },
    { label: dict.nav_contact, href: "#contact", key: "contact" },
  ];

  const toggleLang = () => setLang(lang === "en" ? "ar" : "en");

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#F7F5F0]/70 border-b hairline" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 md:px-10 h-[72px]">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3">
          <svg width="26" height="26" viewBox="0 0 40 40" fill="none" aria-hidden>
            <circle cx="20" cy="20" r="19" stroke="#1A1918" strokeWidth="1.2" />
            <path d="M28 20a8 8 0 1 1-9.6-7.84 6.4 6.4 0 0 0 9.6 7.84z" fill="#1A1918" />
          </svg>
          <div className="leading-none">
            <div className="serif italic text-[18px] tracking-tight text-[#1A1918]">
              Burj Al Qamar
            </div>
            <div className="label mt-1 text-[9px] opacity-70 text-[#1A1918]">
              Trading F.Z.E · UAE
            </div>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.key}
              href={l.href}
              data-testid={`nav-link-${l.key}`}
              className="link-under label text-[11px] text-[#1A1918]"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <button
            onClick={toggleLang}
            data-testid="nav-lang-toggle"
            className="label text-[11px] px-3 py-2 border hairline hover:bg-[#1A1918] hover:text-[#F7F5F0] transition-colors duration-500 text-[#1A1918]"
            aria-label="Toggle language"
          >
            {dict.nav_locale_switch}
          </button>
          <a
            href={`${API}/catalogue.pdf`}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-cta-download"
            className="inline-flex items-center gap-2 px-4 py-3 border border-[#1A1918] text-[#1A1918] label text-[10px] hover:bg-[#1A1918] hover:text-[#F7F5F0] transition-colors duration-500"
          >
            {dict.nav_cta_download}
          </a>
          <a
            href="#contact"
            data-testid="nav-cta-request"
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#1A1918] text-[#F7F5F0] label text-[10px] hover:bg-[#B56B45] transition-colors duration-500"
          >
            {dict.nav_cta_request}
            <span aria-hidden>{isRTL ? "←" : "→"}</span>
          </a>
        </div>

        <div className="md:hidden flex items-center gap-3">
          <button
            onClick={toggleLang}
            data-testid="nav-lang-toggle-m"
            className="label text-[11px] text-[#1A1918]"
          >
            {dict.nav_locale_switch}
          </button>
          <button
            data-testid="nav-menu-toggle"
            className="label text-[11px] text-[#1A1918]"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? dict.nav_close : dict.nav_menu}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#F7F5F0] border-t hairline"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a
                  key={l.key}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="serif italic text-3xl text-[#1A1918]"
                  data-testid={`mnav-link-${l.key}`}
                >
                  {l.label}
                </a>
              ))}
              <a
                href={`${API}/catalogue.pdf`}
                target="_blank"
                rel="noreferrer"
                onClick={() => setOpen(false)}
                className="mt-2 inline-block border border-[#1A1918] text-[#1A1918] px-5 py-3 label text-[10px] w-fit"
                data-testid="mnav-cta-download"
              >
                {dict.nav_cta_download}
              </a>
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                className="mt-2 inline-block bg-[#1A1918] text-[#F7F5F0] px-5 py-3 label text-[10px] w-fit"
                data-testid="mnav-cta-request"
              >
                {dict.nav_cta_request} {isRTL ? "←" : "→"}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
